/**
 * ReviewInbox — the reviewer's unread replies and @mentions.
 *
 * The worker has written these rows since threaded comments shipped, but no
 * client ever read them: a reviewer who was replied to, or named with an
 * @mention, was only told if they happened to reopen that photo's comment
 * sheet. This is the missing consumer of GET /share/:token/notifications.
 *
 * Deliberately small. It is an unread count and a jump list, not an inbox with
 * its own state model — the durable truth stays in the comment thread, and this
 * only points at it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import {
  fetchNotifications,
  markNotificationsRead,
  type ReviewNotification,
} from '../api/client'

interface ReviewInboxProps {
  shareToken: string
  /** Jump to the photo a notification points at. */
  onOpenPhoto: (photoId: string) => void
}

function labelFor(n: ReviewNotification): string {
  return n.type === 'reply' ? 'Someone replied to your comment' : 'You were mentioned in a comment'
}

export function ReviewInbox({ shareToken, onOpenPhoto }: ReviewInboxProps) {
  const [items, setItems] = useState<ReviewNotification[]>([])
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const next = await fetchNotifications(shareToken)
      if (!cancelled) setItems(next)
    }
    void load()
    // A reviewer leaves the tab open while the studio answers. Refresh when the
    // tab regains focus rather than polling on a timer — the same trigger the
    // photo viewer already uses to re-fetch comments.
    const onFocus = () => void load()
    window.addEventListener('focus', onFocus)
    return () => {
      cancelled = true
      window.removeEventListener('focus', onFocus)
    }
  }, [shareToken])

  // Close on outside click so the panel behaves like every other popover.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      // Opening is the acknowledgement — clear the badge, but keep the rows
      // on screen so the reviewer can still act on what they just read.
      if (!wasOpen && items.length > 0) void markNotificationsRead(shareToken)
      return !wasOpen
    })
  }, [items.length, shareToken])

  const unread = items.length
  if (unread === 0 && !open) return null

  return (
    <div ref={panelRef} className="relative shrink-0" data-testid="review-inbox">
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        aria-expanded={open}
        data-testid="review-inbox-bell"
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/18 text-white shadow-lg backdrop-blur-md transition hover:bg-white/28"
      >
        <Bell size={15} strokeWidth={2.2} aria-hidden />
        {unread > 0 && (
          <span
            data-testid="review-inbox-badge"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#e0554b] px-1 text-[10px] font-bold leading-none text-white"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          data-testid="review-inbox-panel"
          className="absolute right-0 top-12 z-30 w-72 overflow-hidden rounded-xl border border-black/10 bg-white text-left shadow-2xl"
        >
          {items.length === 0 ? (
            <p
              data-testid="review-inbox-empty"
              className="px-4 py-5 text-[13px] text-neutral-500"
            >
              Nothing new right now.
            </p>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id} className="border-b border-black/5 last:border-b-0">
                  <button
                    type="button"
                    data-testid={`review-inbox-item-${n.id}`}
                    disabled={!n.photoId}
                    onClick={() => {
                      if (!n.photoId) return
                      setOpen(false)
                      onOpenPhoto(n.photoId)
                    }}
                    className="w-full px-4 py-3 text-left text-[13px] text-neutral-800 transition hover:bg-neutral-50 disabled:cursor-default disabled:text-neutral-400"
                  >
                    {labelFor(n)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
